/**
 * Les critères du journal de rapprochement : la règle, sans réseau.
 *
 * Ce module décide **ce qu'une saisie veut dire** — un IBAN ? une date ? un
 * numéro de facture ? un mot-clé ? — et rend un critère normalisé. La
 * passerelle le traduit ensuite en requête.
 *
 * ## Pourquoi la règle vit ici
 *
 * Même raison que `devis.ts` et `paiement/stripe.ts` :
 * `gateway.supabase.ts` importe le client Supabase, qui **lève au chargement**
 * quand les variables d'environnement manquent. Rien de ce qu'il contient ne
 * s'éprouve. Une règle qu'on veut tester vit à côté, pas dedans.
 *
 * ## Le défaut que ce module existe pour empêcher
 *
 * Les deux onglets lisaient **cent lignes** et filtraient dans le navigateur.
 * Mesuré le 6 septembre 2026 : la file compte 8 715 lignes, le journal 2 493
 * affectations. Chercher un IBAN côté navigateur n'aurait donc interrogé que
 * les cent dernières, et rendu « rien trouvé » sur un IBAN présent deux mille
 * lignes plus loin.
 *
 * **Un écran qui dit « rien trouvé » alors que la ligne existe est pire qu'un
 * écran sans recherche** : on en tire une conclusion. Le filtrage se fait donc
 * en base, contre `v_reconciliation_journal`.
 */

import { centimesSaisis } from './montant';

/** De quel côté du compte la ligne se trouve. */
export type Sens = 'entree' | 'depense';

/** Ce sur quoi la liste est triée. */
export type Tri =
  /** Du plus ancien au plus récent. Le tri par défaut : on traite dans l'ordre. */
  | 'date_ancien'
  /** Du plus récent au plus ancien. */
  | 'date_recent'
  /** Du plus gros montant au plus petit : placer l'argent le plus vite. */
  | 'montant_decroissant';

export const TRI_PAR_DEFAUT: Tri = 'date_ancien';

/** Comment un tri se traduit en colonne et en sens. */
export const COLONNES_DE_TRI: Record<Tri, { colonne: string; ascendant: boolean }> = {
  date_ancien: { colonne: 'date_valeur', ascendant: true },
  date_recent: { colonne: 'date_valeur', ascendant: false },
  montant_decroissant: { colonne: 'montant_cents', ascendant: false },
};

export const LIBELLES_DE_TRI: Record<Tri, string> = {
  date_ancien: 'Du plus ancien au plus récent',
  date_recent: 'Du plus récent au plus ancien',
  montant_decroissant: 'Du plus gros montant au plus petit',
};

/**
 * Le journal a un tri de plus : **l'ordre des décisions**.
 *
 * ## Pourquoi il n'est pas dans la liste de la file
 *
 * Demandé le 19 septembre 2026 : « Si je mets hors périmètre un versement par
 * erreur, il est très difficile de le retrouver rapidement ou d'annuler la
 * dernière saisie. Il faudrait que dans Rapprochés on voie les derniers qui
 * ont été rapprochés de base. »
 *
 * Les autres tris portent sur la **date de valeur** — quand l'argent est passé
 * en banque. Celui-ci porte sur `decide_le`, **quand quelqu'un a tranché**. Ce
 * sont deux temps différents : un virement de juin 2025 classé il y a trente
 * secondes est ancien pour le premier et tout récent pour le second. Défaire
 * un geste demande le second.
 *
 * Il n'a aucun sens dans la file de rapprochement — un paiement qui attend n'a
 * précisément pas été décidé — d'où deux listes plutôt qu'une. Les mélanger
 * aurait donné à la file une option qui, faute de colonne, serait retombée en
 * silence sur la date de valeur : un tri qui ment sur ce qu'il fait.
 */
export type TriDuJournal = Tri | 'decision_recente';

/** Les derniers gestes d'abord : c'est ce qu'on regarde pour en défaire un. */
export const TRI_PAR_DEFAUT_JOURNAL: TriDuJournal = 'decision_recente';

export const COLONNES_DE_TRI_JOURNAL: Record<TriDuJournal, { colonne: string; ascendant: boolean }> = {
  ...COLONNES_DE_TRI,
  decision_recente: { colonne: 'decide_le', ascendant: false },
};

export const LIBELLES_DE_TRI_JOURNAL: Record<TriDuJournal, string> = {
  decision_recente: 'Les derniers rapprochés d\u2019abord',
  ...LIBELLES_DE_TRI,
};

/**
 * Ce qu'une saisie de recherche s'est révélée être.
 *
 * La forme compte, parce qu'elle change la requête : une date se compare à
 * `date_valeur`, un IBAN à `compte_contrepartie`, et tout le reste va au champ
 * `recherche` de la vue.
 */
export type FormeDeRecherche = 'vide' | 'date' | 'iban' | 'montant' | 'texte';

export interface Recherche {
  forme: FormeDeRecherche;
  /** La valeur normalisée, prête pour la requête. */
  valeur: string;
  /**
   * Les centimes, quand la saisie se lit comme un montant.
   *
   * Demandé le 19 septembre 2026, pour retrouver un versement qu'on vient de
   * classer par erreur : « ou alors de pouvoir rechercher par montant ».
   */
  centimes?: number;
  /** Ce que l'écran annonce avoir compris. */
  explication: string | null;
}

/**
 * La forme d'un montant cherché : au plus six chiffres, au plus deux décimales.
 *
 * **Six et non sept**, et c'est la seule subtilité : un numéro de facture en
 * porte sept. `2601466` reste donc du texte, alors que `232049,10` — le plus
 * gros mouvement de la production — se lit bien comme un montant.
 *
 * Aucun séparateur de milliers : `1.234` est ambigu — 1,234 ou 1 234 ? — et
 * une recherche n'a pas à trancher une ambiguïté en silence. `1234,56`
 * fonctionne, et l'écran dit ce qu'il a compris.
 */
const MONTANT_CHERCHE = /^\d{1,6}([.,]\d{1,2})?$/;

/**
 * Une date, dans les formes qu'un Belge tape vraiment.
 *
 * `12/03/2026`, `12-03-2026`, `12.03.2026` et `2026-03-12` mènent tous au même
 * jour. L'ordre jour/mois est celui de `fr-BE` — c'est ce que l'écran affiche,
 * donc c'est ce qu'on relit.
 */
function lireDate(saisie: string): string | null {
  const t = saisie.trim();

  /* Déjà au format de la base. */
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const belge = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(t);
  if (belge) {
    const jour = belge[1].padStart(2, '0');
    const mois = belge[2].padStart(2, '0');
    /*
      On vérifie les bornes, sans aller jusqu'au calendrier : `31/02/2026`
      n'existe pas, mais le refuser demanderait de connaître février. La base
      rendra zéro ligne, ce qui est la bonne réponse.
    */
    if (Number(mois) < 1 || Number(mois) > 12) return null;
    if (Number(jour) < 1 || Number(jour) > 31) return null;
    return `${belge[3]}-${mois}-${jour}`;
  }

  return null;
}

/**
 * Un IBAN, avec ou sans les espaces que la banque met.
 *
 * On accepte un début : `BE643631` doit trouver le compte, parce que personne
 * ne recopie vingt-quatre caractères pour chercher.
 *
 * ## Deux lettres puis deux CHIFFRES, et c'est le test qui l'a imposé
 *
 * La première version demandait deux lettres suivies de quatre caractères
 * alphanumériques. `Dupont` passait : `DUPONT`, c'est bien deux lettres et
 * quatre caractères. Un nom de famille partait donc chercher dans la colonne
 * des IBAN, où il ne pouvait rien trouver — la pire forme d'échec, puisque la
 * ligne existe.
 *
 * Un IBAN commence par le code pays **puis deux chiffres de contrôle**
 * (ISO 13616). C'est ce qui le distingue d'un mot, et c'est ce qu'on exige.
 *
 * Un fragment sans code pays — `36311005` — n'est donc pas reconnu ici, et
 * c'est sans conséquence : il tombe en recherche textuelle, et le champ
 * `recherche` de la vue **contient l'IBAN**. La recherche aboutit quand même.
 */
function lireIban(saisie: string): string | null {
  const compact = saisie.replace(/[\s.]/g, '').toUpperCase();
  if (compact.length < 6) return null;
  if (!/^[A-Z]{2}\d{2}[0-9A-Z]{2,}$/.test(compact)) return null;
  return compact;
}

/**
 * Ce que la saisie veut dire.
 *
 * L'ordre des essais n'est pas arbitraire : une date ne peut pas être un IBAN,
 * mais `BE64` pourrait ressembler à un mot-clé. Le plus spécifique d'abord, le
 * texte libre en dernier — c'est lui qui accepte tout.
 */
export function lireRecherche(saisie: string | null | undefined): Recherche {
  const t = (saisie ?? '').trim();
  if (t.length === 0) return { forme: 'vide', valeur: '', explication: null };

  const date = lireDate(t);
  if (date) {
    return {
      forme: 'date', valeur: date,
      explication: `Les mouvements du ${t}`,
    };
  }

  const iban = lireIban(t);
  if (iban) {
    return {
      forme: 'iban', valeur: iban,
      explication: `Les mouvements dont le compte contient « ${iban} »`,
    };
  }

  /*
    Un montant. Il passe AVANT la limite de trois caractères : « 68 » ne
    discrimine rien comme texte, mais désigne très exactement les mouvements de
    68,00 EUR — et c'est le geste demandé, retrouver ce qu'on vient de classer
    par erreur.

    Le texte est cherché EN PLUS du montant dès qu'il atteint trois caractères.
    Les deux lectures sont légitimes — « 2603 » peut être 26,03 EUR comme un
    fragment de numéro — et il n'y a aucune raison d'en sacrifier une : la
    requête pose les deux, et l'écran dit qu'il a fait les deux.
  */
  const sansEspaces = t.replace(/[\s\u00a0\u202f]/g, '').replace(/\u20ac$/, '');
  if (MONTANT_CHERCHE.test(sansEspaces)) {
    const centimes = centimesSaisis(sansEspaces);
    if (centimes !== null) {
      const montant = `${(centimes / 100).toFixed(2).replace('.', ',')} \u20ac`;
      return {
        forme: 'montant',
        valeur: t,
        centimes,
        explication: t.length >= 3
          ? `Les mouvements de ${montant}, ou dont le texte contient \u00ab\u00a0${t}\u00a0\u00bb`
          : `Les mouvements de ${montant}`,
      };
    }
  }

  /*
    Deux caractères ne discriminent rien sur 2 500 lignes : la recherche
    rendrait la liste entière en laissant croire qu'elle a filtré. On le dit
    plutôt que de faire semblant.
  */
  if (t.length < 3) {
    return {
      forme: 'vide', valeur: '',
      explication: 'Trois caractères au minimum pour chercher.',
    };
  }

  return {
    forme: 'texte', valeur: t,
    explication: `Communication, nom, note ou numéro contenant « ${t} »`,
  };
}

/** Les critères complets d'une consultation du journal. */
export interface CriteresDuJournal {
  /** `null` = les deux sens. */
  sens: Sens | null;
  tri: TriDuJournal;
  recherche: string;
  /** Combien de lignes on demande. */
  taille: number;
  /** À partir de quelle ligne, pour la pagination. */
  depuis: number;
}

export const TAILLE_DE_PAGE = 50;

export function criteresParDefaut(): CriteresDuJournal {
  return {
    sens: null,
    /* Les derniers gestes d'abord : c'est ce qu'on regarde pour en défaire un. */
    tri: TRI_PAR_DEFAUT_JOURNAL,
    recherche: '',
    taille: TAILLE_DE_PAGE,
    depuis: 0,
  };
}

/**
 * La phrase qui dit ce que la liste montre — et **ce qu'elle ne montre pas**.
 *
 * Une liste tronquée qui ne le dit pas est le défaut d'origine : cent lignes
 * sur huit mille, sans un mot. Le total vient du `count` de la base, jamais de
 * la longueur du tableau reçu.
 */
export function phraseDuJournal(
  affichees: number,
  total: number | null,
  centimes: number | null,
  euros: (c: number) => string,
): string {
  if (total === null) return `${affichees} ligne(s) affichée(s).`;
  if (total === 0) return 'Aucune ligne ne correspond.';

  const montant = centimes !== null ? `, pour ${euros(centimes)}` : '';
  if (affichees >= total) {
    return `${total} ligne(s)${montant}.`;
  }
  return `${affichees} ligne(s) affichée(s) sur ${total}${montant}.`;
}

/** Le libellé d'un sens, tel qu'il s'écrit à l'écran. */
export function libelleSens(sens: Sens): string {
  return sens === 'entree' ? 'Entrées' : 'Dépenses';
}

/**
 * Une ligne du journal, telle que la vue la rend.
 *
 * Les noms sont ceux de `v_reconciliation_journal` — un renommage de plus
 * n'apporterait qu'une occasion de se tromper.
 */
export interface LigneDuJournal {
  sens: Sens;
  ligneId: string;
  decideLe: string;
  dateValeur: string;
  montantCents: number;
  mouvementCents: number;
  cible: string;
  methode: string;
  notes: string | null;
  paymentId: string | null;
  communication: string | null;
  compteContrepartie: string | null;
  nomContrepartie: string | null;
  pieceNumero: string | null;
  pieceType: 'facture' | 'note_de_credit' | null;
  piecePdf: string | null;
  famille: string | null;
}

/** Ce qu'une consultation rend. */
export interface PageDuJournal {
  lignes: LigneDuJournal[];
  /** Le total réel, du `count` de la base. `null` si la base ne l'a pas donné. */
  total: number | null;
  /** La somme des montants de la PAGE. Pas du total : la base ne l'agrège pas ici. */
  centimesAffiches: number;
}
